import { format } from 'date-fns'
import omit from 'lodash-es/omit'

import APPLICANTS_SNAPSHOT from '../../../../rental-portal-mock-data/applicants.json'
import {
  HTTP_STATUS_CONFLICT,
  HTTP_STATUS_INTERNAL_SERVER_ERROR,
  HTTP_STATUS_NOT_FOUND,
  HTTP_STATUS_UNPROCESSABLE_ENTITY,
} from '../../../constants/api'
import { EMPTY_LIST } from '../../../constants/common'
import { DATE_API_DATA_FORMAT_SHORT } from '../../../constants/date'
import {
  RENTAL_PORTAL_MOCK_LATENCY,
  RENTAL_PORTAL_MOCK_STATE,
} from '../../../constants/rentalPortalMockState'
import {
  getRentalPortalMockAvatarColor,
  getRentalPortalMockMediaUrl,
} from '../../../utils/rentalPortalMockMedia'
import { hasRentalPortalMockState } from '../../../utils/rentalPortalMockState'
import {
  getMockWaitingOnColleague,
  MOCK_LISTINGS,
  MOCK_PORTAL_LOGO_PATH,
  toMockSessionUser,
  toMockWaitingOnApplicant,
} from '../../RentalPortalListing'
import {
  APPLICANT_PAYLOAD_FIELD,
  APPLICANT_STAGE,
} from '../RentalPortalApplicant.constants'

import { getMockApplicantListMatch } from './RentalPortalApplicantMatch.mocks'

const MOCK_LISTING_APPLICANTS_READ_LATENCY = 300

const MOCK_LISTING_APPLICANTS_READ_ERROR = {
  response: { status: HTTP_STATUS_INTERNAL_SERVER_ERROR },
}

export const MOCK_APPLICANT_SOURCE_LABEL_KEY = {
  ImmoScout24: 'RentalPortal.ApplicantSourceImmoScout24Key',
  Immowelt: 'RentalPortal.ApplicantSourceImmoweltKey',
  Manual: 'RentalPortal.ApplicantSourceManualKey',
  'Online form': 'RentalPortal.ApplicantSourceOnlineFormKey',
}

const MOCK_APPLICANT_WAITING_ON = {
  applicant: 'applicant',
  colleague: 'colleague',
  you: 'you',
}

const MOCK_APPLICANT_WAITING_ON_LABEL_KEY = {
  [MOCK_APPLICANT_WAITING_ON.applicant]:
    'RentalPortal.ApplicantWaitingOnApplicantKey',
  [MOCK_APPLICANT_WAITING_ON.colleague]:
    'RentalPortal.ApplicantWaitingOnColleagueKey',
  [MOCK_APPLICANT_WAITING_ON.you]: 'RentalPortal.ApplicantWaitingOnYouKey',
}

function getMockApplicantWaitingOnUser({ applicant, currentUser, who }) {
  switch (who) {
    case MOCK_APPLICANT_WAITING_ON.you:
      return toMockSessionUser(currentUser)
    case MOCK_APPLICANT_WAITING_ON.colleague:
      return getMockWaitingOnColleague(applicant.id)
    case MOCK_APPLICANT_WAITING_ON.applicant:
      return toMockWaitingOnApplicant(applicant)
    default:
      return null
  }
}

const MOCK_APPLICANT_REFERENCE_PREFIX = 'Applicant #'

export function toMockApplicantReference(reference) {
  return reference?.startsWith(MOCK_APPLICANT_REFERENCE_PREFIX)
    ? reference.slice(MOCK_APPLICANT_REFERENCE_PREFIX.length)
    : reference
}

const MOCK_APPLICANT_REFERENCE_CODE_LENGTH = 4

function toMockApplicantReferenceCode(id) {
  return id
    .toString(16)
    .toUpperCase()
    .padStart(MOCK_APPLICANT_REFERENCE_CODE_LENGTH, '0')
}

const MOCK_APPLICANT_DEGRADED_FIELD = {
  attention: APPLICANT_PAYLOAD_FIELD.attention,
  match: APPLICANT_PAYLOAD_FIELD.match,
  phase: 'phase',
  stage: 'stage',
}

export function getDegradedMockApplicantFields() {
  return [
    hasRentalPortalMockState(RENTAL_PORTAL_MOCK_STATE.noAttention)
      ? MOCK_APPLICANT_DEGRADED_FIELD.attention
      : null,
    hasRentalPortalMockState(RENTAL_PORTAL_MOCK_STATE.noMatching)
      ? MOCK_APPLICANT_DEGRADED_FIELD.match
      : null,
    hasRentalPortalMockState(RENTAL_PORTAL_MOCK_STATE.noPhase)
      ? MOCK_APPLICANT_DEGRADED_FIELD.phase
      : null,
    hasRentalPortalMockState(RENTAL_PORTAL_MOCK_STATE.noStage)
      ? MOCK_APPLICANT_DEGRADED_FIELD.stage
      : null,
  ].filter(Boolean)
}

function getDegradedMockListingApplicantFields() {
  return getDegradedMockApplicantFields().filter(
    field => field !== MOCK_APPLICANT_DEGRADED_FIELD.attention
  )
}

function getMockApplicantManageRight(id) {
  if (hasRentalPortalMockState(RENTAL_PORTAL_MOCK_STATE.readOnly)) {
    return false
  }

  return hasRentalPortalMockState(RENTAL_PORTAL_MOCK_STATE.reducedManager)
    ? id % 2 === 0
    : true
}

function getMockApplicantPermissions({ id, permissions }) {
  const canManage = getMockApplicantManageRight(id)

  return {
    ...permissions,
    can_approve:
      canManage &&
      hasRentalPortalMockState(RENTAL_PORTAL_MOCK_STATE.canApprove),
    can_archive: permissions.can_archive && canManage,
    can_manage: canManage,
    can_restore: permissions.can_restore && canManage,
    can_reveal_identity:
      canManage &&
      hasRentalPortalMockState(RENTAL_PORTAL_MOCK_STATE.canRevealIdentity),
    can_view_sensitive: false,
  }
}

export function getMockApplicantsPolicy() {
  return {
    rental_erp_handoff_enabled: !hasRentalPortalMockState(
      RENTAL_PORTAL_MOCK_STATE.noHandoff
    ),
  }
}

export function toServedMockApplicant(applicant, currentUser) {
  const who = applicant.attention?.waiting_on?.who
  const reference = toMockApplicantReference(applicant.reference)

  return {
    ...applicant,
    attention: {
      ...applicant.attention,
      waiting_on: {
        ...applicant.attention?.waiting_on,
        label: MOCK_APPLICANT_WAITING_ON_LABEL_KEY[who] ?? null,
        user: getMockApplicantWaitingOnUser({
          applicant: { ...applicant, reference },
          currentUser,
          who,
        }),
      },
    },
    avatar_color: getRentalPortalMockAvatarColor(applicant.id),
    match: getMockApplicantListMatch(applicant),
    permissions: getMockApplicantPermissions(applicant),
    reference,
    source:
      MOCK_APPLICANT_SOURCE_LABEL_KEY[applicant.source] ?? applicant.source,
    source_logo_url: getRentalPortalMockMediaUrl(
      MOCK_PORTAL_LOGO_PATH[applicant.source]
    ),
  }
}

function toMockListingApplicantsById(applicants) {
  return [...applicants]
    .sort(
      (first, second) =>
        second.received.localeCompare(first.received) || first.id - second.id
    )
    .reduce((acc, applicant) => {
      const served = acc.get(applicant.listing_id) ?? EMPTY_LIST

      return acc.set(applicant.listing_id, [...served, applicant])
    }, new Map())
}

const MOCK_LISTING_APPLICANTS_BY_ID = toMockListingApplicantsById(
  APPLICANTS_SNAPSHOT.filter(applicant => !applicant.archived)
)

const MOCK_LISTING_APPLICANTS_SERVED_IDS = new Set()

function withMockLatency(listingId) {
  const isRefresh = MOCK_LISTING_APPLICANTS_SERVED_IDS.has(listingId)

  MOCK_LISTING_APPLICANTS_SERVED_IDS.add(listingId)

  const isSlow =
    hasRentalPortalMockState(RENTAL_PORTAL_MOCK_STATE.loading) ||
    (isRefresh &&
      hasRentalPortalMockState(RENTAL_PORTAL_MOCK_STATE.slowRefresh))

  return new Promise(resolve => {
    setTimeout(
      resolve,
      isSlow ? RENTAL_PORTAL_MOCK_LATENCY : MOCK_LISTING_APPLICANTS_READ_LATENCY
    )
  })
}

export function getMockListingApplicants({
  currentUser,
  listing_id: listingId,
  page,
  page_size: pageSize,
}) {
  return withMockLatency(Number(listingId)).then(() => {
    if (hasRentalPortalMockState(RENTAL_PORTAL_MOCK_STATE.error)) {
      return Promise.reject(MOCK_LISTING_APPLICANTS_READ_ERROR)
    }

    const applicants = hasRentalPortalMockState(RENTAL_PORTAL_MOCK_STATE.empty)
      ? EMPTY_LIST
      : [
          ...getCreatedMockApplicants(Number(listingId)),
          ...(MOCK_LISTING_APPLICANTS_BY_ID.get(Number(listingId)) ??
            EMPTY_LIST),
        ]

    const degradedFields = getDegradedMockListingApplicantFields()

    const servedApplicants = applicants.map(applicant =>
      omit(toServedMockApplicant(applicant, currentUser), degradedFields)
    )

    const pageCount = Math.ceil(applicants.length / pageSize)
    const currentPage = Math.min(page, Math.max(pageCount, 1))
    const firstIndex = (currentPage - 1) * pageSize

    return {
      data: {
        allItems: servedApplicants,
        pagination: {
          page: currentPage,
          page_count: pageCount,
          total: applicants.length,
        },
        results: {
          objects: servedApplicants.slice(firstIndex, firstIndex + pageSize),
        },
      },
    }
  })
}

const MOCK_CREATED_APPLICANTS = []

const MOCK_APPLICANT_CONTACT_BY_ID = new Map()

let nextMockApplicantId = 1000

const MOCK_APPLICANT_MANUAL_SOURCE = 'Manual'

const MOCK_APPLICANT_WRITE_ERROR = {
  response: { status: HTTP_STATUS_INTERNAL_SERVER_ERROR },
}

const MOCK_APPLICANT_LISTING_NOT_FOUND_ERROR = {
  response: { status: HTTP_STATUS_NOT_FOUND },
}

const MOCK_STATE_APPLICANT_VALIDATION_ERROR = 'applicant-validation-error'

const MOCK_STATE_APPLICANT_LISTING_GONE = 'applicant-listing-gone'

const MOCK_APPLICANT_EMAIL_REJECTED_ERROR_KEY =
  'ServerError.RentalApplicantEmailRejectedKey'

const MOCK_APPLICANT_VALIDATION_ERROR = {
  response: {
    data: { errors: { email: [MOCK_APPLICANT_EMAIL_REJECTED_ERROR_KEY] } },
    status: HTTP_STATUS_UNPROCESSABLE_ENTITY,
  },
}

export function getCreatedMockApplicants(listingId) {
  return listingId == null
    ? MOCK_CREATED_APPLICANTS
    : MOCK_CREATED_APPLICANTS.filter(
        applicant => applicant.listing_id === listingId
      )
}

const MOCK_APPLICANT_HOUSEHOLD_SIZES = [1, 2, 3, 4, 5, 6]

export function getMockApplicantHouseholdSizes() {
  return MOCK_APPLICANT_HOUSEHOLD_SIZES
}

function getMockApplicantListingsSource() {
  return MOCK_LISTINGS.filter(listing => !listing.archived)
}

function hasMockApplicantListingMatch(listing, search) {
  return listing.unit_label.toLowerCase().includes(search.trim().toLowerCase())
}

export function getMockApplicantListings({ search }) {
  return withMockApplicantWriteLatency().then(() => {
    if (hasRentalPortalMockState(RENTAL_PORTAL_MOCK_STATE.error)) {
      return Promise.reject(MOCK_LISTING_APPLICANTS_READ_ERROR)
    }

    const items = hasRentalPortalMockState(RENTAL_PORTAL_MOCK_STATE.empty)
      ? EMPTY_LIST
      : getMockApplicantListingsSource().filter(
          listing => !search || hasMockApplicantListingMatch(listing, search)
        )

    return {
      data: {
        items,
        meta: { count: items.length, curr_page: 1, page_count: 1 },
      },
    }
  })
}

function withMockApplicantWriteLatency() {
  const isSlow =
    hasRentalPortalMockState(RENTAL_PORTAL_MOCK_STATE.loading) ||
    hasRentalPortalMockState(RENTAL_PORTAL_MOCK_STATE.saveError)

  return new Promise(resolve => {
    setTimeout(
      resolve,
      isSlow ? RENTAL_PORTAL_MOCK_LATENCY : MOCK_LISTING_APPLICANTS_READ_LATENCY
    )
  })
}

const MOCK_APPLICANT_SEEDED_DUPLICATE_EMAIL = 'lead.duplicate@example.com'

function seedMockApplicantContacts() {
  getMockApplicantListingsSource().forEach(listing => {
    const [newest] = MOCK_LISTING_APPLICANTS_BY_ID.get(listing.id) ?? EMPTY_LIST

    if (newest) {
      MOCK_APPLICANT_CONTACT_BY_ID.set(newest.id, {
        email: MOCK_APPLICANT_SEEDED_DUPLICATE_EMAIL,
      })
    }
  })
}

seedMockApplicantContacts()

function getMockApplicantDuplicate({ email, listingId }) {
  const storedEmail = email.trim().toLowerCase()

  return [
    ...MOCK_CREATED_APPLICANTS,
    ...(MOCK_LISTING_APPLICANTS_BY_ID.get(listingId) ?? EMPTY_LIST),
  ].find(
    applicant =>
      applicant.listing_id === listingId &&
      MOCK_APPLICANT_CONTACT_BY_ID.get(applicant.id)?.email.toLowerCase() ===
        storedEmail
  )
}

function getMockApplicantConflictError(applicant) {
  return {
    response: {
      data: {
        listing_label: applicant.listing_label,
        reference: toMockApplicantReference(applicant.reference),
      },
      status: HTTP_STATUS_CONFLICT,
    },
  }
}

export function addMockApplicant({
  email,
  first_name: firstName,
  household_size: householdSize,
  last_name: lastName,
  listing: listingId,
  message,
  move_in_date: moveInDate,
  phone,
}) {
  return withMockApplicantWriteLatency().then(() => {
    if (hasRentalPortalMockState(RENTAL_PORTAL_MOCK_STATE.saveError)) {
      return Promise.reject(MOCK_APPLICANT_WRITE_ERROR)
    }

    if (hasRentalPortalMockState(MOCK_STATE_APPLICANT_VALIDATION_ERROR)) {
      return Promise.reject(MOCK_APPLICANT_VALIDATION_ERROR)
    }

    const listing = hasRentalPortalMockState(MOCK_STATE_APPLICANT_LISTING_GONE)
      ? null
      : getMockApplicantListingsSource().find(
          item => item.id === Number(listingId)
        )

    if (!listing) {
      return Promise.reject(MOCK_APPLICANT_LISTING_NOT_FOUND_ERROR)
    }

    const duplicate = getMockApplicantDuplicate({
      email,
      listingId: listing.id,
    })

    if (duplicate) {
      return Promise.reject(getMockApplicantConflictError(duplicate))
    }

    const id = nextMockApplicantId

    nextMockApplicantId += 1

    const applicant = {
      archived: false,
      attention: {
        needs_you: true,
        primary_action: null,
        waiting_on: { who: MOCK_APPLICANT_WAITING_ON.you },
      },
      awaiting_approval: false,
      handed_off: false,
      id,
      identity: { masked: true, value: null },
      listing_id: listing.id,
      listing_label: listing.unit_label,
      matching: {
        household_size: householdSize ?? null,
        move_in_date: moveInDate ?? null,
      },
      permissions: {
        can_archive: true,
        can_manage: true,
        can_restore: false,
        can_view: true,
      },
      received: format(new Date(), DATE_API_DATA_FORMAT_SHORT),
      reference: `${MOCK_APPLICANT_REFERENCE_PREFIX}${toMockApplicantReferenceCode(id)}`,
      source: MOCK_APPLICANT_MANUAL_SOURCE,
      stage: APPLICANT_STAGE.pool,
      uk_id: 1,
      unseen: false,
    }

    MOCK_APPLICANT_CONTACT_BY_ID.set(id, {
      email,
      firstName,
      lastName,
      message,
      phone,
    })

    MOCK_CREATED_APPLICANTS.unshift(applicant)

    return { data: applicant }
  })
}
